<template>
  <RightSidebar :isActive.sync="isActive" size="large">
    <template #header>
      <img width="40" :src="ProductIcon" class="mr-4" />
      <div>
        <h4>{{ isNewProduct ? "New " : "" }}Stock product</h4>
        <small v-if="product"> {{ product.number }} • {{ product.name }} </small>
      </div>
    </template>

    <template #content>
      <Loading v-if="isLoading" />
      <div v-else>
        <section class="px-6 pt-6 grid grid-cols-4 gap-x-8">
          <SidebarItem label="Number">
            <vs-input class="w-full" v-model="product.number" :disabled="!isNewProduct" />
          </SidebarItem>

          <!-- <SidebarItem label="Country">
            <vs-select
              v-model="product.countryId"
              class="w-full"
              :disabled="!isNewProduct"
            >
              <vs-select-item
                :key="index"
                :value="item.countryId.id"
                :text="item.name"
                v-for="(item, index) in countries"
              />
            </vs-select>
          </SidebarItem> -->

          <div class="flex flex-col mb-2" v-if="isNewProduct">
            <div class="mb-1 text-sm flex">
              <span>Unlock date</span>
              <vx-tooltip
                text="Enable unlock the stock product at a given date. If no date is given, the stock product is locked"
                position="bottom"
              >
                <feather-icon icon="InfoIcon" svg-classes="h-4 w-4" class="ml-1" />
              </vx-tooltip>
            </div>
            <div class="flex items-center">
              <div class="flex-grow overflow-hidden">
                <v-date-picker
                  v-model="product.unlockDate"
                  mode="dateTime"
                  class="max-w-full"
                  is24hr
                  :attributes="[{ highlight: { fillMode: 'light' }, dates: new Date() }]"
                >
                  <template v-slot="{ inputValue, inputEvents }">
                    <vs-input :value="inputValue" v-on="inputEvents" class="max-w-full mr-0" :disabled="!enableUnlockDateField" />
                  </template>
                </v-date-picker>
              </div>
              <div>
                <vs-switch v-model="enableUnlockDateField" class="ml-2" />
              </div>
            </div>
          </div>

          <SidebarItem
            v-else
            label="Status"
            :tooltip="
              product.inactive && hasUnlockDate
                ? `Will automatically unlock at ${formatDate(
                    product.lockAction.when
                  )}. You can manually unlock it at any given time prior to this`
                : null
            "
          >
            <vs-select v-model="product.inactive" class="w-full">
              <vs-select-item
                :key="item.value"
                :value="item.value"
                :text="item.name"
                v-for="item in [
                  { value: false, name: 'Unlocked' },
                  { value: true, name: 'Locked' }
                ]"
              />
            </vs-select>
          </SidebarItem>

          <!-- <SidebarItem
            label="Created by"
            class="col-span-2"
            v-if="!isNewProduct"
          >
            <code class="text-sm"
              >{{ product.createdBy.username }} at
              {{ product.createdAt | formatDate }}</code
            >
          </SidebarItem> -->
        </section>

        <vs-divider />

        <section class="px-6 grid grid-cols-4 gap-x-8">
          <SidebarItem label="Name" class="col-span-2">
            <vs-input class="w-full" v-model="product.name" />
          </SidebarItem>

          <SidebarItem label="Image" class="col-span-1 row-span-2">
            <div
              v-if="product.imageUrl"
              class="w-32 h-32 rounded border border-solid flex items-center justify-center p-2"
              style="border-color: #eee"
            >
              <img :src="product.imageUrl" class="h-full" v-img:stockProduct />
            </div>
            <div v-else class="w-32 h-32 rounded border border-dashed flex items-center justify-center" style="border-color: #ccc">
              <feather-icon icon="ImageIcon" svg-classes="h-5 w-5" class="opacity-50" />
            </div>
          </SidebarItem>

          <SidebarItem label="Color" class="col-span-1 row-span-2">
            <input
              type="color"
              v-model="product.color"
              class="w-32 h-32 rounded-lg border border-solid custom-color-picker"
              style="border-color: #eee"
              :style="'--color-picker-color:' + product.color + '62'"
            />
          </SidebarItem>

          <SidebarItem label="Vendor">
            <vs-select v-model="product.vendorId" autocomplete class="w-full">
              <vs-select-item :key="index" :value="item.vendorId.id" :text="item.name" v-for="(item, index) in vendors" />
            </vs-select>
          </SidebarItem>

          <SidebarItem label="Vendor item number">
            <vs-input class="w-full" v-model="product.vendorItemNumber" />
          </SidebarItem>

          <SidebarItem label="Alternative vendor">
            <vs-select v-model="product.alternativeVendorId" autocomplete class="w-full">
              <vs-select-item :key="index" :value="item.vendorId.id" :text="item.name" v-for="(item, index) in vendors" />
            </vs-select>
          </SidebarItem>

          <SidebarItem label="Alt. vendor item number">
            <vs-input class="w-full" v-model="product.alternativeVendorItemNumber" />
          </SidebarItem>

          <SidebarItem label="Brand owner" class="col-span-2">
            <vs-input class="w-full" v-model="product.brandOwner" />
          </SidebarItem>

          <SidebarItem label="Tags" class="col-span-full">
            <vs-chips placeholder="New tag" class="w-full py-0" v-model="product.tags">
              <vs-chip @click="removeTag(tag)" :key="tag" v-for="tag in product.tags" class="my-1 ml-2 mr-0" closable>
                {{ tag }}
              </vs-chip>
            </vs-chips>
          </SidebarItem>

          <vs-divider class="col-span-full" />

          <SidebarItem label="Category">
            <vs-select v-model="product.productCategoryId" class="w-full">
              <vs-select-item
                :key="index"
                :value="item.productCategoryId.id"
                :text="item.name"
                v-for="(item, index) in productCategories"
              />
            </vs-select>
          </SidebarItem>

          <!-- <SidebarItem label="Sub category">
            <vs-select v-model="product.productSubCategoryId" class="w-full">
              <vs-select-item key="empty" :value="null" text="None" />
              <vs-select-item
                :key="index"
                :value="item.productSubCategoryId.id"
                :text="item.name"
                v-for="(item, index) in productSubCategories"
              />
            </vs-select>
          </SidebarItem> -->

          <SidebarItem label="Product group">
            <vs-select :disabled="!product.productCategoryId" v-model="product.productGroupId" class="w-full">
              <vs-select-item key="empty" :value="null" text="None" />
              <vs-select-item :key="index" :value="item.stockProductGroupId.id" :text="item.name" v-for="(item, index) in validSubGroups" />
            </vs-select>
          </SidebarItem>

          <SidebarItem label="Pouring category">
            <vs-select v-model="product.pouringCategoryId" class="w-full">
              <vs-select-item
                :key="index"
                :value="item.pouringCategoryId.id"
                :text="item.name"
                v-for="(item, index) in pouringCategories"
              />
            </vs-select>
          </SidebarItem>

          <SidebarItem label="Stock type">
            <vs-select v-model="product.containerType" class="w-full">
              <vs-select-item key="empty" :value="null" text="Other" />
              <vs-select-item :key="item" :value="item" :text="item" v-for="item in stockContainerTypes" />
            </vs-select>
          </SidebarItem>

          <vs-divider class="col-span-full" />

          <SidebarItem label="VAT rate">
            <vs-select v-model="product.taxGroupId" class="w-full">
              <vs-select-item :key="index" :value="item.taxGroupId.id" :text="item.code" v-for="(item, index) in taxGroups" />
            </vs-select>
          </SidebarItem>

          <SidebarItem label="Product account group">
            <vs-select v-model="product.productAccountGroupId" class="w-full" :disabled="true">
              <vs-select-item
                :key="index"
                :value="item.productCategoryId.id"
                :text="item.name"
                v-for="(item, index) in productCategories"
              />
            </vs-select>
          </SidebarItem>

          <SidebarItem label="Item account group">
            <vs-select v-model="product.itemAccountGroupId" class="w-full" :disabled="true">
              <vs-select-item
                :key="index"
                :value="item.productCategoryId.id"
                :text="item.name"
                v-for="(item, index) in productCategories"
              />
            </vs-select>
          </SidebarItem>

          <SidebarItem label="Max allowed stock deviation">
            <NumberInput v-model="product.allowedDeviationPercentage" unit="%" />
          </SidebarItem>

          <vs-divider class="col-span-full" />

          <SidebarItem label="Case size">
            <NumberInput v-model="product.caseSize" :unit="product.productUnit" />
          </SidebarItem>

          <SidebarItem label="Unit of measure">
            <vs-select v-model="product.productUnit">
              <vs-select-item :key="unit" :value="unit" :text="unit" v-for="unit in validUnitOfMeasures" />
            </vs-select>
          </SidebarItem>

          <SidebarItem label="Unit price">
            <NumberInput v-model="product.unitPrice" unit="DKK" />
          </SidebarItem>

          <SidebarItem label="Unit amount in liters">
            <NumberInput v-model="product.productUnitInLiters" unit="liters" />
          </SidebarItem>

          <!-- <vs-divider class="col-span-full" /> -->
        </section>

        <!-- <SidebarItem label="">
          <div class="w-32 mr-2 flex items-center justify-end">2 cl</div>
          <NumberInput v-model="product.unitPrice" unit="DKK" />
        </SidebarItem> -->

        <!-- <h3 class="ml-4 mt-8 mb-2 text-center">
          Default pricings
          <vs-button type="flat" size="small" @click="addDefaultPricing">
            <feather-icon icon="PlusIcon" svgClasses="w-4 h-4 pt-1" />
            <span class="ml-1 text-sm">Add</span>
          </vs-button>
        </h3> -->

        <!-- <div class="flex justify-center">
          <div>
            <vs-table :data="product.defaultPricings">
              <template slot="thead">
                <vs-th sort-key="amount">Amount</vs-th>
                <vs-th sort-key="price">Price</vs-th>
                <vs-th>Cogs</vs-th>
                <vs-th></vs-th>
              </template>

              <template slot-scope="{ data }">
                <tbody>
                  <vs-tr
                    :data="tr"
                    :key="index"
                    v-for="(tr, index) in data"
                    class="cursor-default"
                  >
                    <vs-td>
                      <NumberSelectInput
                        v-model="tr.amount"
                        :unit.sync="tr.productUnit"
                        :units="units"
                        style="width: 142px"
                      />
                    </vs-td>
                    <vs-td>
                      <NumberInput
                        v-model="tr.price"
                        unit="DKK"
                        style="width: 142px"
                      />
                    </vs-td>
                    <vs-td>
                      {{
                        calculateCogs(
                          tr.productUnit,
                          tr.amount,
                          tr.price,
                          product.unitPrice,
                          product.productUnitInLiters
                        )
                      }}
                      %
                    </vs-td>
                    <vs-td class="whitespace-no-wrap">
                      <feather-icon
                        icon="TrashIcon"
                        class="cursor-pointer"
                        svgClasses="w-5 h-5 hover:text-danger stroke-current"
                        @click.stop="removeDefaultPricing(index)"
                      />
                    </vs-td>
                  </vs-tr>
                </tbody>
              </template>
            </vs-table>

            <div class="flex justify-end mt-2 mr-1"></div>
          </div>
        </div> -->
      </div>
    </template>

    <template #footer>
      <vs-button @click="submitData" :disabled="!isFormValid">{{ isNewProduct ? "Create" : "Save" }}</vs-button>
      <vs-button type="border" color="danger" class="ml-6" @click="isActive = false">Cancel</vs-button>
      <vs-button v-if="!isNewProduct" type="border" class="ml-auto" @click="assignToTestVenue">Assign to LGD venue</vs-button>
    </template>
  </RightSidebar>
</template>

<script>
import ProductIcon from "@/assets/images/product-icon.png";
import moment from "moment";
import { mapGetters } from "vuex";
import VenueSelector from "@/components/VenueSelector.vue";
import RightSidebar from "@/components/Custom/RightSidebar.vue";
import SidebarItem from "@/components/Custom/SidebarItem.vue";
import NumberSelectInput from "@/components/Custom/NumberSelectInput.vue";
import NumberInput from "@/components/Custom/NumberInput.vue";
import { calculateCogs } from "@/utils/posUtils.js";

export default {
  components: {
    VenueSelector,
    RightSidebar,
    SidebarItem,
    NumberSelectInput,
    NumberInput
  },
  data() {
    return {
      ProductIcon,
      calculateCogs,

      isActive: false,
      isLoading: true,

      isNewProduct: false,
      product: null,

      enableUnlockDateField: false,
      hoveringLockField: false
    };
  },
  computed: {
    ...mapGetters({
      activeVenue: "venues/activeVenue",
      activeCountry: "venues/activeCountry",
      countries: "pos/countries/countries",
      productCategories: "pos/countrySettings/stockCategories",
      pouringCategories: "pos/countrySettings/pouringCategories",
      taxGroups: "pos/countrySettings/taxGroups",
      units: "pos/countrySettings/units",
      vendors: "pos/vendors/vendors",
      stockContainerTypes: "pos/countrySettings/stockContainerTypes"
      // productCategories: "productCategories/productCategories",
    }),
    validUnitOfMeasures() {
      return ["pcs", "btl", "can", "unit", "glass", "bag"]; // TODO: Get from API
    },
    validSubGroups() {
      if (!this.product) return [];
      var category = this.productCategories.find(x => x.productCategoryId.id === this.product.productCategoryId);

      if (category) return category.validProductGroups;
      else return [];
    },
    hasUnlockDate() {
      return (
        this.product.lockAction &&
        this.product.lockAction.when &&
        moment(this.product.lockAction.when).year() > 2000 && // Over year 2000 (aka over DateTime.MinValue in C#)
        moment(this.product.lockAction.when).isAfter(moment())
      );
    },
    isFormValid() {
      const p = this.product;
      if (
        !p ||
        !p.number ||
        !p.name ||
        !p.productUnit ||
        !p.productCategoryId ||
        !p.pouringCategoryId ||
        !p.vendorId ||
        !p.vendorItemNumber ||
        !p.taxGroupId
      )
        return false;
      return true;
    }
  },
  methods: {
    async show(stockProductId) {
      this.isNewProduct = false;
      this.isActive = true;
      this.isLoading = true;

      this.product = await this.$store.dispatch("pos/stockProducts/getStockProduct", stockProductId);
      console.log("Product:", this.product);
      await this.loadExternalData();

      this.isLoading = false;
    },
    async showEmptyProduct() {
      this.isNewProduct = true;

      this.product = {
        countryId: this.activeCountry.id,
        number: "",
        name: "",
        color: "#ffbabe",
        caseSize: 0,
        unitPrice: 0,
        productUnit: "pcs",
        productUnitInLiters: 0,
        imageUrl: "",
        inactive: true,
        productCategoryId: "",
        productSubCategoryId: "",
        productGroupId: "",
        productAccountGroupId: "",
        productItemGroupId: "",
        pouringCategoryId: "",
        vendorId: "",
        vendorItemNumber: "",
        alternativeVendorId: "",
        alternativeVendorItemNumber: "",
        taxGroupId: "",
        brandOwner: "",
        alcoholByVolume: 0,
        allowedDeviationPercentage: 0,
        tags: [],
        discountAmountPerUnit: 0,
        discountPercentPerUnit: 0,
        createdBy: null,
        createdAt: null,

        defaultPricings: []
      };

      this.isActive = true;
      this.isLoading = true;
      await this.loadExternalData();
      this.isLoading = false;
    },

    async loadExternalData() {
      await Promise.all([
        // this.$store.dispatch("pos/countries/getCountries"),
        this.$store.dispatch("pos/countrySettings/getCountrySettings"),
        this.$store.dispatch("pos/vendors/getVendors")
        // this.$store.dispatch("productCategories/getProductCategories"),
        // this.$store.dispatch("taxGroups/getTaxGroups"),
      ]);
    },

    addDefaultPricing() {
      this.product.defaultPricings.push({
        amount: 2,
        productUnit: "cl",
        price: 0
      });
    },

    removeDefaultPricing(index) {
      this.product.defaultPricings.splice(index, 1);
    },

    formatDate(date) {
      return moment(date).format("DD-MM-YYYY HH:mm");
    },

    async submitData() {
      console.log("Submitting DTO", this.product);
      let newCreatedProductResponse = null;

      try {
        if (this.isNewProduct) {
          newCreatedProductResponse = await this.$store.dispatch("pos/stockProducts/createStockProduct", {
            stockProductDto: this.product
          });
          console.log("New created product", newCreatedProductResponse);
        } else {
          await this.$store.dispatch("pos/stockProducts/updateStockProduct", {
            stockProductId: this.product.id,
            stockProductDto: this.product
          });
        }

        this.product = null;
        this.isActive = false;
        this.isLoading = true;

        console.log("New created product", newCreatedProductResponse);

        // If we just created a new product. Show the sidebar again with the product
        if (newCreatedProductResponse) {
          this.show(newCreatedProductResponse);
        }

        this.$store.dispatch("pos/stockProducts/getStockProducts");

        this.$vs.notify({
          title: "Success",
          text: `Successfully ${this.isNewProduct ? "created new" : "updated"} stock product`
        });
      } catch (error) {
        this.$vs.notify({
          title: "Error",
          color: "danger",
          text: `Failed to ${this.isNewProduct ? "create" : "update"} stock product: ${error}`
        });

        this.isLoading = false;
      }
    },

    async assignToTestVenue() {
      console.log("Want to assign to test venue");
      try {
        await this.$store.dispatch("pos/assignedStockProducts/assignStockProductToVenue", {
          venueId: "cfae18d5-a9d7-451b-aa9e-d253456ff64d",
          stockProductId: this.product.id
        });

        this.$vs.notify({
          title: "Success",
          text: `Successfully assigned stock product to LGD venue`
        });
      } catch (error) {
        this.$vs.notify({
          title: "Error",
          color: "danger",
          text: `Failed to assign stock product to LGD venue: ${error}`
        });
      }
    }
  }
};
</script>

<style lang="scss">
.custom-color-picker {
  appearance: none;
  border: none;
  background-color: transparent;

  &::-webkit-color-swatch {
    border: none;
    border-radius: 12px;
    box-shadow: 2px 4px 12px 0 var(--color-picker-color);
  }
  &::-webkit-color-swatch-wrapper {
    padding: 16px;
  }
}
</style>